"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.validatePersonId = validatePersonId;
exports.fetchPersonAccountV2 = fetchPersonAccountV2;
exports.fetchPersonProfileV2 = fetchPersonProfileV2;
exports.putPersonAccountV2 = putPersonAccountV2;
exports.putPersonProfileV2 = putPersonProfileV2;
exports.fetchPersonProfileExperiences = fetchPersonProfileExperiences;
exports.fetchPersonProfileEducations = fetchPersonProfileEducations;
exports.fetchPersonProfileSkills = fetchPersonProfileSkills;
exports.fetchSkillsDictionary = fetchSkillsDictionary;
exports.savePersonV2AccountAndProfile = savePersonV2AccountAndProfile;
exports.throwOnSaveErrors = throwOnSaveErrors;
exports.fetchPersonProjectAssignments = fetchPersonProjectAssignments;
exports.fetchPersonAccountEvents = fetchPersonAccountEvents;
exports.savePersonProjectAssignments = savePersonProjectAssignments;
exports.buildAccountPayload = buildAccountPayload;
exports.saveProjectAssignments = saveProjectAssignments;
const skillsDictionaryApi_1 = require("../Admin/SkillsDictionary/skillsDictionaryApi");
const MainSetupReact_1 = __importDefault(require("../React/MainSetupReact"));
const ToolsFetch_1 = __importDefault(require("../React/Tools/ToolsFetch"));
/**
 * Waliduje personId przed wywolaniem endpointow v2.
 * Rzuca Error jesli personId nie jest dodatnia liczba calkowita.
 *
 * @param personId - identyfikator osoby (z repository.currentItems[0].id)
 * @param context - opcjonalny kontekst do komunikatu bledu (np. "GET account", "PUT profile")
 * @returns personId (typ number) -- zwraca wartosc dla wygody chainowania
 * @throws Error jesli personId jest undefined/null, nie jest liczba, <= 0 lub nie jest calkowita
 */
function validatePersonId(personId, context) {
    if (personId == null) {
        throw new Error(`personId jest wymagany${context ? ` (${context})` : ""}`);
    }
    if (typeof personId !== "number" || !Number.isFinite(personId)) {
        throw new Error(`personId musi byc liczba, otrzymano: ${typeof personId}${context ? ` (${context})` : ""}`);
    }
    if (!Number.isInteger(personId) || personId <= 0) {
        throw new Error(`personId musi byc dodatnia liczba calkowita, otrzymano: ${personId}${context ? ` (${context})` : ""}`);
    }
    return personId;
}
/**
 * Pobiera dane account v2 dla osoby.
 * @returns PersonAccountV2Payload lub null jesli brak account (404)
 */
async function fetchPersonAccountV2(personId) {
    const validId = validatePersonId(personId, "GET account");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/account`;
    try {
        const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        });
        return result;
    }
    catch (error) {
        // 404 lub brak danych -- zwracamy null
        console.warn("fetchPersonAccountV2: brak account dla personId=%d: %o", validId, error);
        return null;
    }
}
/**
 * Pobiera dane profile v2 dla osoby.
 * @returns PersonProfileV2Record lub null jesli brak profile (404)
 */
async function fetchPersonProfileV2(personId) {
    const validId = validatePersonId(personId, "GET profile");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/profile`;
    try {
        const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
            method: "GET",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
        });
        return result;
    }
    catch (error) {
        // 404 lub brak danych -- zwracamy null
        console.warn("fetchPersonProfileV2: brak profile dla personId=%d: %o", validId, error);
        return null;
    }
}
/**
 * Zapisuje dane account v2 dla osoby (PUT).
 * @returns zaktualizowany PersonAccountV2Payload
 * @throws Error jesli zapis sie nie powiedzie
 */
async function putPersonAccountV2(personId, payload) {
    const validId = validatePersonId(personId, "PUT account");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/account`;
    const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return result;
}
/**
 * Zapisuje dane profile v2 dla osoby (PUT).
 * @returns zaktualizowany PersonProfileV2Payload
 * @throws Error jesli zapis sie nie powiedzie
 */
async function putPersonProfileV2(personId, payload) {
    const validId = validatePersonId(personId, "PUT profile");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/profile`;
    const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
    });
    return result;
}
async function searchProfileModule(personId, modulePath, orConditions, context) {
    const validId = validatePersonId(personId, context);
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/profile/${modulePath}/search`;
    try {
        const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orConditions }),
        });
        return result;
    }
    catch (error) {
        console.warn("searchProfileModule: blad %s dla personId=%d: %o", modulePath, validId, error);
        return [];
    }
}
async function fetchPersonProfileExperiences(personId, orConditions = []) {
    return searchProfileModule(personId, "experiences", orConditions, "POST experiences/search");
}
async function fetchPersonProfileEducations(personId, orConditions = []) {
    return searchProfileModule(personId, "educations", orConditions, "POST educations/search");
}
async function fetchPersonProfileSkills(personId, orConditions = []) {
    return searchProfileModule(personId, "skills", orConditions, "POST skills/search");
}
/**
 * Pobiera slownik skilli z wyszukiwaniem.
 * @param searchText - opcjonalny tekst do wyszukiwania
 * @returns tablica SkillDictionaryRecord
 */
async function fetchSkillsDictionary(searchText) {
    const url = `${MainSetupReact_1.default.serverUrl}v2/skills/search`;
    const trimmedSearchText = searchText?.trim();
    const orConditions = trimmedSearchText ? [{ searchText: trimmedSearchText }] : [];
    try {
        const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
            method: "POST",
            credentials: "include",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ orConditions }),
        });
        return result.map(skillsDictionaryApi_1.mapSkillDictionaryDtoToModel);
    }
    catch (error) {
        console.warn("fetchSkillsDictionary: blad pobierania skilli: %o", error);
        return [];
    }
}
/**
 * Payload bez ani jednego zdefiniowanego pola nie ma czego zapisać — nie wysyłamy żądania.
 *
 * Dla konta jest to wręcz konieczne: serwer odrzuca pusty PUT (400 "Brak danych konta do
 * aktualizacji" — patrz `hasAccountUpsertWriteField` w PS-nodeJS/src/persons/PersonsRouters.ts),
 * a moduł Persons woła zapis z pustym `{}` przy KAŻDEJ edycji osoby. Dopóki błędy szły tylko
 * do konsoli, nikt tego nie widział; skoro teraz trafiają do modala, pusty payload musi
 * po prostu nie generować żądania.
 *
 * Dla profilu serwer pustego PUT-a nie odrzuca, tylko zakłada PUSTY wiersz `PersonProfiles`
 * — czyli robi zapis "na zapas" za każdą edycję osoby. Nie jest potrzebny: moduły profilu
 * (wykształcenie, doświadczenie, umiejętności) tworzą ten wiersz same, leniwie, przy
 * pierwszym realnym wpisie (`ensurePersonProfileId` w ich repozytoriach), a `GET .../profile`
 * dla brakującego wiersza zwraca `null`, co UI obsługuje ("Brak profilu").
 */
function hasAnyFieldToWrite(payload) {
    return Object.values(payload).some((value) => value !== undefined);
}
/**
 * Wspolna funkcja zapisu account + profile v2 z ujednolicona obsluga bledow.
 * Kolejnosc: account -> profile (sekwencyjnie).
 * Funkcja nie rzuca -- kazdy blad jest logowany i zwracany w tablicy errors, zeby
 * wywolujacy sam zdecydowal, co z nim zrobic. Modale osob i uzytkownikow systemu
 * podaja te tablice do `throwOnSaveErrors`, dzieki czemu blad widac w pasku bledu.
 *
 * @param personId - identyfikator osoby
 * @param accountPayload - payload account (pusty {} => PUT jest pomijany)
 * @param profilePayload - payload profile (pusty {} => PUT jest pomijany)
 * @param callerContext - kontekst wywolania do logow (np. "StaffMembers", "Persons")
 */
async function savePersonV2AccountAndProfile(personId, accountPayload, profilePayload, callerContext) {
    const result = { account: null, profile: null, errors: [] };
    // Account pierwszy -- musi istniec przed profile
    if (hasAnyFieldToWrite(accountPayload)) {
        try {
            result.account = await putPersonAccountV2(personId, accountPayload);
        }
        catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            result.errors.push(`Konto systemowe: ${msg}`);
            console.warn("[%s] savePersonV2: blad PUT account dla personId=%d: %s", callerContext, personId, msg);
        }
    }
    // Profile drugi
    if (hasAnyFieldToWrite(profilePayload)) {
        try {
            result.profile = await putPersonProfileV2(personId, profilePayload);
        }
        catch (error) {
            const msg = error instanceof Error ? error.message : String(error);
            result.errors.push(`Profil osoby: ${msg}`);
            console.warn("[%s] savePersonV2: blad PUT profile dla personId=%d: %s", callerContext, personId, msg);
        }
    }
    if (result.errors.length > 0) {
        console.warn("[%s] savePersonV2: zapis v2 zakonczony z bledami dla personId=%d: %o", callerContext, personId, result.errors);
    }
    return result;
}
/**
 * Zamienia zebrane błędy domknięcia zapisu w wyjątek, który `GeneralModal` pokaże
 * w pasku błędu (`handleSubmitRepository` łapie go i ustawia `errorMessage`).
 *
 * Tak dotąd ginął konflikt SystemEmail (`PERSON_ACCOUNT_SYSTEM_EMAIL_CONFLICT`):
 * dane osoby zapisywały się, konto NIE, a modal zamykał się jak po udanym zapisie —
 * użytkownik nie miał żadnego sygnału, że coś nie przeszło.
 *
 * Wołać PO callbacku odświeżającym listę: dane legacy są już w bazie, więc lista
 * ma pokazać ich nową wersję niezależnie od tego, że domknięcie się nie udało.
 */
function throwOnSaveErrors(errors, savedWhat = "Dane osoby") {
    if (errors.length === 0)
        return;
    throw new Error(`${savedWhat} zapisano, ale część danych NIE została zapisana:\n${errors.join("\n")}`);
}
/** Projekty przypisane osobie (rola CONTRACT_WORKER). */
async function fetchPersonProjectAssignments(personId) {
    const validId = validatePersonId(personId, "GET project-assignments");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/project-assignments`;
    const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
        method: "GET",
        credentials: "include",
    });
    return result?.assignments ?? [];
}
/**
 * ROD-3: ostatnie zmiany konta osoby (kto, kiedy, co) - sekcja „Ostatnie zmiany konta"
 * w modalu uprawnień. Trasa za bramką administratora/menedżera; nie-tablica (np. błąd 403)
 * daje pustą listę, żeby modal nie padł - sekcja pokaże komunikat.
 */
async function fetchPersonAccountEvents(personId, limit = 20) {
    const validId = validatePersonId(personId, "GET account-events");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/account-events?limit=${limit}`;
    const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
        method: "GET",
        credentials: "include",
    });
    return Array.isArray(result) ? result : [];
}
/**
 * Ustawia komplet przypisań osoby. Pusta tablica czyści przypisania - tak wygląda
 * zmiana roli na inną niż pracownik kontraktowy.
 */
async function savePersonProjectAssignments(personId, projectOurIds) {
    const validId = validatePersonId(personId, "PUT project-assignments");
    const url = `${MainSetupReact_1.default.serverUrl}v2/persons/${validId}/project-assignments`;
    const result = await ToolsFetch_1.default.fetchJsonWithSafeError(url, {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ projectOurIds }),
    });
    return result?.assignments ?? [];
}
/**
 * Pola konta systemowego (PersonAccounts) wysyłane trasą v2. Dodanie i edycja wysyłają
 * dokładnie to samo - jedna funkcja zamiast dwóch kopii, które potrafią się rozjechać.
 *
 * PAYLOAD MUSI ZOSTAĆ PEŁNY - nie okrawać go "bo legacy i tak zapisze":
 * przy DODAWANIU legacy niczego nie zapisuje. `PersonsController.addFromDto` robi
 * `delete person.systemRoleId; delete person.systemEmail;` przed zapisem, więc
 * `POST /person` tworzy samą osobę, a konto zakłada WYŁĄCZNIE to wywołanie v2.
 * Bez roli i e-maila powstałoby konto, którym nie da się zalogować.
 *
 * Przy EDYCJI rolę i e-mail zapisuje dziś także legacy `PUT /person/:id`
 * (`DEFAULT_EDIT_FIELDS`) - to znany, przejściowy duplikat. Kierunek docelowy zgodny
 * z `@deprecated` w backendzie: legacy przestaje pisać konto (zawężenie `fieldsToUpdate`
 * w modalu edycji), a v2 przejmuje całość. NIE odwrotnie.
 *
 * `fidmanEnabled` zawsze jawnie, także przy false: brak pola serwer czyta jako
 * "nie ruszaj flagi", więc odznaczenie bez tej linii nigdy by nie doszło. Tę flagę
 * backend przyjmuje wyłącznie tą trasą (`ACCOUNT_FIELDS_WRITABLE_ONLY_VIA_ACCOUNT_ROUTE`),
 * bo tylko tu kolejkuje się push `user.upsert` do FIDmana.
 */
function buildAccountPayload(values) {
    return {
        systemRoleId: values.systemRoleId ? Number(values.systemRoleId) : undefined,
        systemEmail: values.systemEmail || undefined,
        fidmanEnabled: Boolean(values.fidmanEnabled),
    };
}
/**
 * Zapisuje przypisania projektów po zapisaniu konta. Dla ról spoza zakresowych
 * (pracownik kontraktowy, klient) wysyła pustą listę - dzięki temu zmiana roli na inną
 * odbiera dostęp do projektów, zamiast zostawiać go po cichu w bazie.
 */
async function saveProjectAssignments(person) {
    if (!person?.id)
        return;
    if (!MainSetupReact_1.default.isProjectScopedRoleId(person.systemRoleId ?? undefined)) {
        await savePersonProjectAssignments(person.id, []);
        return;
    }
    const selected = person._projectAssignments;
    // Brak pola to NIE to samo co pusty wybór. Gdyby formularz go nie dowiózł,
    // pusta lista skasowałaby istniejące przypisania - a zapis, który po cichu
    // kasuje dane, jest gorszy niż zapis, który ich nie ruszy.
    if (!Array.isArray(selected))
        return;
    await savePersonProjectAssignments(person.id, selected.map((project) => project.ourId));
}
